import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useUserAvatar } from '@/hooks/useUserAvatar';
import { Camera, Upload, X, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface UploadAvatarDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UploadAvatarDialog({ open, onOpenChange }: UploadAvatarDialogProps) {
  const { avatarUrl, isUploading, uploadAvatar, refreshAvatar } = useUserAvatar();
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Criar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    const result = await uploadAvatar(selectedFile);
    if (result) {
      await refreshAvatar();
      handleClose();
    }
  };

  const handleClose = () => {
    setPreview(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onOpenChange(false);
  };

  const handleRemove = () => {
    setPreview(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Alterar Foto de Perfil</DialogTitle>
          <DialogDescription>
            Selecione uma nova foto para o seu perfil. Formatos aceitos: JPG, PNG, GIF. Tamanho máximo: 5MB.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Preview do avatar atual e novo */}
          <div className="flex items-center justify-center gap-4">
            <div className="flex flex-col items-center gap-2">
              <p className="text-sm text-muted-foreground">Atual</p>
              <Avatar className="h-20 w-20">
                <AvatarImage src={avatarUrl || undefined} />
                <AvatarFallback className="bg-primary/10">
                  <Camera className="h-8 w-8 text-primary" />
                </AvatarFallback>
              </Avatar>
            </div>
            
            {preview && (
              <>
                <div className="text-2xl text-muted-foreground">→</div>
                <div className="flex flex-col items-center gap-2">
                  <p className="text-sm text-muted-foreground">Nova</p>
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={preview} />
                    <AvatarFallback className="bg-primary/10">
                      <Camera className="h-8 w-8 text-primary" />
                    </AvatarFallback>
                  </Avatar>
                </div>
              </>
            )}
          </div>

          {/* Input de arquivo */}
          <div className="space-y-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              id="avatar-upload"
            />
            <label htmlFor="avatar-upload">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={isUploading}
                asChild
              >
                <span>
                  <Upload className="mr-2 h-4 w-4" />
                  Selecionar Foto
                </span>
              </Button>
            </label>
          </div>

          {/* Botões de ação */}
          <div className="flex gap-2">
            {selectedFile && (
              <Button
                type="button"
                variant="outline"
                onClick={handleRemove}
                disabled={isUploading}
                className="flex-1"
              >
                <X className="mr-2 h-4 w-4" />
                Remover
              </Button>
            )}
            <Button
              type="button"
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
              className="flex-1"
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Salvar
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
